/* eslint-disable react/jsx-props-no-spreading */
import type { Role } from 'payments-api/model';
import {
  useGetRoles,
  usePostProducts,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { CreateRoleModal } from './CreateRoleModal';

const CREATE_ROLE_SENTINEL = '__create_role__';

type RoleField = 'paidByRole' | 'paidToRole';

type FormData = {
  code: string;
  description: string;
};

type CreateProductModalProps = {
  onClose: () => void;
  onCreated: (productCode: string) => void;
};

export const CreateProductModal = ({
  onClose,
  onCreated,
}: CreateProductModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postProductMutation = usePostProducts();
  const { data: rolesData } = useGetRoles();
  const [paidByRole, setPaidByRole] = useState('');
  const [paidToRole, setPaidToRole] = useState('');
  const [creatingRoleFor, setCreatingRoleFor] = useState<RoleField | null>(
    null
  );

  const roles: Role[] = rolesData?.data ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { code: '', description: '' },
  });

  const handleRoleChange = useCallback((field: RoleField, value: string) => {
    if (value === CREATE_ROLE_SENTINEL) {
      setCreatingRoleFor(field);
    } else if (field === 'paidByRole') {
      setPaidByRole(value);
    } else {
      setPaidToRole(value);
    }
  }, []);

  const handleRoleCreated = useCallback(
    (roleName: string) => {
      if (creatingRoleFor === 'paidByRole') {
        setPaidByRole(roleName);
      } else if (creatingRoleFor === 'paidToRole') {
        setPaidToRole(roleName);
      }
      setCreatingRoleFor(null);
    },
    [creatingRoleFor]
  );

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postProductMutation.mutateAsync({
          data: {
            code: formData.code.trim(),
            ...(formData.description.trim() && {
              description: formData.description.trim(),
            }),
            ...(paidByRole && { paid_by_roles: [{ name: paidByRole }] }),
            ...(paidToRole && { paid_to_roles: [{ name: paidToRole }] }),
          },
        });
        await queryClient.invalidateQueries({ queryKey: ['/products'] });
        showToast(
          <Toast type="success" message="Product created successfully" />
        );
        onCreated(formData.code.trim());
        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create product. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create product: ${errorMessage}`}
          />
        );
      }
    },
    [
      postProductMutation,
      paidByRole,
      paidToRole,
      queryClient,
      showToast,
      onCreated,
      onClose,
    ]
  );

  const roleOptions = [
    ...roles.map((r) => ({ value: r.role, label: r.role })),
    { value: CREATE_ROLE_SENTINEL, label: 'New+' },
  ];

  const hasAtLeastOneRole =
    paidByRole.trim() !== '' || paidToRole.trim() !== '';
  const canSubmit =
    isValid && hasAtLeastOneRole && !postProductMutation.isPending;

  return (
    <>
      <Modal
        data-testid="create-product-modal"
        onClose={onClose}
        width="sm"
        onSubmit={handleSubmit(onSubmit)}
        header={<div className="text-main">Create Product</div>}
        body={
          <div className="flex flex-col space-y-f2">
            <ModalField
              hasError={!!errors.code}
              label="Code"
              labelWidth="w-f12"
            >
              <ModalTextInput
                data-testid="product-code-input"
                placeholder="Product code"
                {...register('code', {
                  required: 'Product code is required',
                  validate: (value) =>
                    value.trim() !== '' || 'Product code cannot be empty',
                })}
              />
            </ModalField>
            <ModalField label="Description" labelWidth="w-f12">
              <ModalTextInput
                data-testid="product-description-input"
                placeholder="Description (optional)"
                {...register('description')}
              />
            </ModalField>
            <ModalField label="Paid By Role" labelWidth="w-f12" overflow>
              <Dropdown
                options={roleOptions}
                onChange={(v) => handleRoleChange('paidByRole', v)}
                displayValue={paidByRole}
                placeholder="Select a role"
                size="md"
                width="w-full"
                height="h-fit"
                align="left"
                data-testid="product-paid-by-role-dropdown"
              />
            </ModalField>
            <ModalField label="Paid To Role" labelWidth="w-f12" overflow>
              <Dropdown
                options={roleOptions}
                onChange={(v) => handleRoleChange('paidToRole', v)}
                displayValue={paidToRole}
                placeholder="Select a role"
                size="md"
                width="w-full"
                height="h-fit"
                align="left"
                data-testid="product-paid-to-role-dropdown"
              />
            </ModalField>
            {!hasAtLeastOneRole && (
              <span className="text-orange text-md pl-f12">
                At least one role (Paid By or Paid To) is required.
              </span>
            )}
          </div>
        }
        footer={
          <div className="flex flex-row justify-between w-full">
            <FormButtons
              type="secondary"
              cancelButtonLabel="Cancel"
              submitButtonLabel={
                postProductMutation.isPending ? 'Creating...' : 'Create'
              }
              onCancel={onClose}
              onSubmit={handleSubmit(onSubmit)}
              disabled={!canSubmit}
            />
          </div>
        }
      />
      {creatingRoleFor !== null && (
        <CreateRoleModal
          onClose={() => setCreatingRoleFor(null)}
          onCreated={handleRoleCreated}
        />
      )}
    </>
  );
};
